import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { NotificationService } from 'ui-components/services/notification-service';
import { emptyArray } from 'ui-components/default-props/empty';

import Global from '../../../../store/global-selectors';
import ModalPortal from '../../../../components/modal-portal';
import { resendClientInvite } from '../../api/fulfillment-delegation';
import { errorCodesFromBackend, ClientStatus } from '../constants';
import messages from '../messages';
import style from '../style.module.less';
import InviteClientForm, { clientInviteFormFields } from './form';
import { invalidEmailErrorProps, invalidClientNameErrorProps } from './errors';
import { successNotification, generalErrorNotification, duplicateClientEmailErrorNotification } from './notifications';

const getClientNameErrorProps = (proposedClientName, existingClients, pendingInvite) => {
  const sanitizedName = proposedClientName?.toLowerCase().trim();
  const isValid = !existingClients.some((existingClient) => {
    return existingClient?.clientName === sanitizedName && existingClient.id !== pendingInvite.externalInviteId;
  });
  if (isValid) {
    return;
  }
  return invalidClientNameErrorProps;
};

const getClientEmailErrorProps = (proposedClientEmail, existingClients, pendingInvite) => {
  const sanitizedEmail = proposedClientEmail?.toLowerCase().trim();
  const isValid = !existingClients.some((existingClient) => {
    return existingClient?.clientEmail === sanitizedEmail && existingClient.id !== pendingInvite.externalInviteId;
  });
  if (isValid) {
    return;
  }
  return invalidEmailErrorProps;
};

const fieldValidatorMap = {
  [clientInviteFormFields.recipientName]: getClientNameErrorProps,
  [clientInviteFormFields.recipientEmail]: getClientEmailErrorProps,
};

const ResendInviteModal = ({ existingClients = emptyArray, pendingInvite, onClose, onAddSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const sellerCompanyName = useSelector(Global.selectSellerCompanyName) ?? '';
  const [formState, setFormState] = useState({
    [clientInviteFormFields.recipientName]: pendingInvite.recipientName,
    [clientInviteFormFields.senderName]: sellerCompanyName,
    [clientInviteFormFields.recipientEmail]: pendingInvite.recipientEmail,
  });
  const [validationMessages, setValidationError] = useState({});

  const validateField = useCallback(
    ({ target: { name, value } }) => {
      setValidationError({
        ...validationMessages,
        [clientInviteFormFields[name]]: fieldValidatorMap[name]?.(value, existingClients, pendingInvite),
      });
    },
    [existingClients, validationMessages, pendingInvite],
  );

  const onInputChange = useCallback(
    ({ target: { value, name } }) => {
      setFormState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      if (validationMessages[name]) {
        setValidationError((prevState) => ({
          ...prevState,
          [name]: undefined,
        }));
      }
    },
    [validationMessages],
  );

  const onInviteClientAsync = useCallback(() => {
    setIsLoading(true);
    resendClientInvite(pendingInvite.externalInviteId, formState)
      .then(() => {
        NotificationService.showNotification(successNotification);
        onAddSuccess();
        onClose();
      })
      .catch((e) => {
        const errorCode = e?.response?.data?.Errors?.[0]?.Code;
        let errorNotification = generalErrorNotification;
        if (errorCode === errorCodesFromBackend.duplicateClientEmail) {
          errorNotification = duplicateClientEmailErrorNotification;
          setValidationError({
            ...validationMessages,
            [clientInviteFormFields.recipientEmail]: invalidEmailErrorProps,
          });
        }
        NotificationService.showNotification(errorNotification);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [formState, onAddSuccess, onClose, validationMessages, pendingInvite]);

  const shouldDisableButton =
    Object.values(formState).some((value) => !value) || Object.values(validationMessages).some((bool) => bool) || isLoading;

  return (
    <ModalPortal onClose={onClose}>
      <Modal size='small'>
        <ModalHeader>
          <FormattedMessage {...messages.modalHeader} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          {isLoading && (
            <div className={style['loading-spinner-wrapper']}>
              <LoadingIndicator className={style['loading-spinner']} />
            </div>
          )}
          {!isLoading && (
            <InviteClientForm
              formData={formState}
              onFormChange={onInputChange}
              onValidate={validateField}
              validationMessages={validationMessages}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>
            <FormattedMessage {...messages.cancel} theme='tertiary' />
          </Button>
          <Button disabled={shouldDisableButton} onClick={onInviteClientAsync} theme='primary'>
            <FormattedMessage {...messages.addClient} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

ResendInviteModal.propTypes = {
  existingClients: PropTypes.arrayOf(
    PropTypes.shape({
      recipientName: PropTypes.string,
      recipientEmail: PropTypes.string,
      status: PropTypes.oneOf([ClientStatus.ACTIVE, ClientStatus.PENDING]),
      id: PropTypes.string,
    }),
  ),
  pendingInvite: PropTypes.shape({
    recipientName: PropTypes.string,
    recipientEmail: PropTypes.string,
    externalInviteId: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onAddSuccess: PropTypes.func.isRequired,
};

export default ResendInviteModal;
